import {
  useGetEOBCount,
  useGetUserClaimTariffStatusesCount,
  useListEobs,
  useListProvidersBySite,
  useListRemittanceBySite,
  useListSitesByOrganization,
  useListStatus,
  prefetchPaginateClaimsByUser,
  usePaginateClaimsByUser,
} from 'api/resource';

import { useQueryClient } from '@tanstack/react-query';
import { useListPayors } from 'api/resource/payor';
import { useGetActionRequestsCount } from 'api/resource/actionManagement';
import { useCombineStates } from 'hooks/useCombineStates';
import { useProviderAllSites } from 'hooks/useProviderAllSites';
import { useRemoteFeatureFlag } from 'hooks/useRemoteFeatureFlag';
import { useEffect, useMemo, useState } from 'react';
import { useCurrentUserStore } from 'store/UserStore';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';

export const useProviderClaimManagementData = () => {
  const queryClient = useQueryClient();
  const {
    hasSiteScope,
    isAllSites,
    providersResult: providersByUserResult,
    siteID,
    sites: providerSites,
  } = useProviderAllSites();
  const { organizationID, id: userID } = useCurrentUserStore();
  const { pagination, setPagination } = useClaimPaginationStore();
  const isNextPagePrefetchEnabled = useRemoteFeatureFlag(
    'claim_table_next_page_prefetch'
  );
  const columnFiltersWithoutStatusID = useMemo(
    () =>
      (pagination.columnFilters ?? []).filter(
        (filter) => filter.id !== 'statusID'
      ),
    [pagination.columnFilters]
  );

  const paginatedClaimsResult = usePaginateClaimsByUser(
    userID,
    siteID,
    pagination.pageIndex,
    pagination.pageSize,
    columnFiltersWithoutStatusID,
    pagination.globalFilter,
    pagination.sorting
  );

  useEffect(() => {
    if (!isNextPagePrefetchEnabled || !userID || !hasSiteScope) return;
    void prefetchPaginateClaimsByUser(
      queryClient,
      userID,
      siteID,
      pagination.pageIndex + 1,
      pagination.pageSize,
      columnFiltersWithoutStatusID,
      pagination.globalFilter,
      pagination.sorting
    );
  }, [
    isNextPagePrefetchEnabled,
    queryClient,
    userID,
    siteID,
    hasSiteScope,
    pagination.pageIndex,
    pagination.pageSize,
    columnFiltersWithoutStatusID,
    pagination.globalFilter,
    pagination.sorting,
  ]);

  const claimStatusesCountResult = useGetUserClaimTariffStatusesCount(
    userID,
    siteID,
    columnFiltersWithoutStatusID,
    pagination.globalFilter
  );
  const remittanceResult = useListRemittanceBySite({ siteID });
  const selectedSiteID = Array.isArray(siteID) ? undefined : siteID;
  const providersBySiteResult = useListProvidersBySite({
    siteID: selectedSiteID,
  });
  const providersResult = isAllSites
    ? providersByUserResult
    : providersBySiteResult;
  const sitesByOrganizationResult = useListSitesByOrganization({
    orgID: organizationID,
  });
  const [eobLookupsRequested, setEobLookupsRequested] = useState(false);
  const shouldLoadEobLookups =
    eobLookupsRequested ||
    (pagination.columnFilters ?? []).some(
      (filter) => filter.id === 'debits.eobs'
    );
  const eobsResult = useListEobs({ enabled: shouldLoadEobLookups });
  const eobCountsResult = useGetEOBCount({ enabled: eobLookupsRequested });

  const statusesResult = useListStatus({});
  const payorsResult = useListPayors();
  const actionRequestsCountResult = useGetActionRequestsCount({
    userID,
    siteID,
  });

  const { isLoading, isError, isFetching, errorMessage } = useCombineStates([
    paginatedClaimsResult,
    claimStatusesCountResult,
    providersResult,
    remittanceResult,
    ...(isAllSites ? [] : [sitesByOrganizationResult]),
    statusesResult,
    payorsResult,
    actionRequestsCountResult,
  ]);

  return {
    claims: {
      payorTariffCounts: paginatedClaimsResult.data?.payorTariffCounts ?? {},
      data: paginatedClaimsResult.data?.data || [],
      total: paginatedClaimsResult.data?.total || 0,
      nextPage: paginatedClaimsResult.data?.nextPage || 0,
      invalidData: paginatedClaimsResult.data?.invalidData || [],
      totalAmountSubmitted:
        paginatedClaimsResult.data?.totalAmountSubmitted || 0,
      totalAmountPaid: paginatedClaimsResult.data?.totalAmountPaid || 0,
      totalAmountOutstanding:
        paginatedClaimsResult.data?.totalAmountOutstanding || 0,
    },
    claimStatusesCount: claimStatusesCountResult.data,
    providers: providersResult.data || [],
    remittances: remittanceResult.data || [],
    sites: isAllSites ? providerSites : sitesByOrganizationResult.data || [],
    status: statusesResult.data || [],
    eobs: eobsResult.data || [],
    payors: payorsResult.data || [],
    eobCounts: eobCountsResult.data || {},
    actionRequestsCount: actionRequestsCountResult.data || 0,
    isLoading,
    isError: isError || eobsResult.isError || eobCountsResult.isError,
    isFetching,
    errorMessage:
      errorMessage ||
      eobsResult.error?.message ||
      eobCountsResult.error?.message ||
      '',
    invalidData: paginatedClaimsResult.data?.invalidData,
    pagination,
    setPagination,
    refetchClaims: paginatedClaimsResult.refetch,
    refetchStatusesCount: claimStatusesCountResult.refetch,
    loadEobLookups: () => setEobLookupsRequested(true),
  };
};
